import { createContext, useContext, useEffect, useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'

import SilentFullPageErrorFallback from '@/components/SilentFullPageErrorFallback'
import { ensureValidAccessToken, sessionExpired } from '@/lib/auth/authSession'
import { AuthService } from '@/lib/services/AuthService'
import { UserAndCompany } from '../definitions/api.types'
import { useQueryErrorListener } from '@/lib//hooks/useQueryErrorListener'

const auth = new AuthService()

export interface AuthState {
  user: UserAndCompany
  active: boolean
  organizationType: OrganizationType
}

export const SET_USER = 'setUser'
export const UPDATE_USER = 'updateUser'
export const REFRESH_USER = 'refreshUser'

export enum OrganizationType {
  CUSTOMER = 'customer',
  RESELLER = 'reseller',
}

export const reducer = (state: any, action: any) => {
  switch (action.type) {
    case SET_USER:
      return {
        ...state,
        user: action.payload,
        active: true,
        organizationType: action.payload.is_reseller
          ? OrganizationType.RESELLER
          : OrganizationType.CUSTOMER,
      }

    case UPDATE_USER:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload,
        },
      }

    case REFRESH_USER:
      return {
        ...state,
        refetchTrigger: state.refetchTrigger + 1,
      }

    default:
      return state
  }
}

export const initialState = {
  active: false,
  refetchTrigger: 0,
}

export type Action =
  | {
      type: typeof SET_USER
      payload: UserAndCompany
    }
  | { type: typeof UPDATE_USER; payload: Partial<UserAndCompany> }
  | { type: typeof REFRESH_USER }

interface AuthContextType {
  state: AuthState
  dispatch: React.Dispatch<Action>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

AuthContext.displayName = 'AuthContext'

function AuthProvider(props: any) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const fetchUser = async () => {
    const accessToken = await ensureValidAccessToken()
    if (!accessToken) {
      return null
    }

    const user = await auth.getUser()
    if (!user) return null

    dispatch({
      type: SET_USER,
      payload: user.data,
    })

    return user
  }

  const { isPending, isSuccess, isError, error } = useQuery({
    queryKey: ['getUser'],
    queryFn: fetchUser,
    gcTime: 0,
    retry: 0,
    refetchOnWindowFocus: false,
    meta: {
      error: { ignore: true },
    },
  })

  useQueryErrorListener(({ query, response }) => {
    const key = query.queryKey[0]
    if (key === 'getUser') {
      if (response?.status === 401) {
        sessionExpired()
      }
    }
  })

  useEffect(() => {
    if (state.refetchTrigger > 0) {
      fetchUser()
    }
  }, [state.refetchTrigger])

  if (isPending) {
    return <div />
  }

  if (isError) {
    return <SilentFullPageErrorFallback error={error} />
  }

  if (isSuccess) {
    return <AuthContext.Provider value={[state, dispatch]} {...props} />
  }

  throw new Error('Unhandled status')
}

/**
 * @returns [AuthState, React.Dispatch<Action>]
 */
function useAuth(): any {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider')
  }
  return context
}

export { AuthProvider, useAuth }
